import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { PlaidContext } from "../../context/plaidContext"
import { useCachedQueryData, usePlaidLayer, usePostHog } from "../../hooks"
import {
  dismissLink,
  LinkExit,
  LinkSuccess,
} from "../ReactNativePlaidLinkSDK/ReactNativePlaidLinkSDK"
import { Sentry } from "../../utils"
import { Platform } from "react-native"
import { useAccountLink, useAccountLinkSession, useAccountLinkToken } from "../../api/mutations"
import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { PlaidFallbackOnboardingLink } from "./PlaidFallbackOnboardingLink"
import {
  clearPlaidOAuthResume,
  consumePlaidOAuthReturnUri,
  PlaidProviderOAuthResume,
} from "../../utils/plaidOAuthResume"

type LinkProps = { linkToken: string | null; tokenId: string | null }

/** What phone auth prefetches into the cache alongside the verification id. */
type CachedLinkToken = { tokenId: string; linkToken: string } | null

/** Shared by phone-auth writers and PlaidProvider readers — clear per flow instance. */
export const PLAID_FLOW_PHONE_QUERY_KEY = ["signUpPhoneNumber"] as const
export const PLAID_FLOW_LINK_TOKEN_QUERY_KEY = ["onboardingLinkToken"] as const

export function clearPlaidFlowCache(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: PLAID_FLOW_PHONE_QUERY_KEY })
  queryClient.removeQueries({ queryKey: PLAID_FLOW_LINK_TOKEN_QUERY_KEY })
}

type LinkProvidedProps = {
  children: ReactNode
  setOpenLayer: (ref: () => void) => void
  setOpenPlaid: (ref: () => void) => void
  handleSuccessAsync: (params: {
    success: LinkSuccess
    tokenId: string
    layerPassed: boolean
  }) => void
  setIsLayerEligible: (isEligible: boolean) => void
  setStartLayerSession: (cb: () => void) => void
  receivedRedirectUri: string | null
  onFallbackStart: () => void
  onFallbackExit: () => void
}

export type PlaidLinkResult = {
  layerPassed: boolean
  institutionName?: string
}

type PlaidProviderProps = PropsWithChildren<{
  /**
   * Where the flow goes after a successful bank link is a flow-machine
   * decision, so the provider only reports the outcome.
   */
  onLinkResult: (result: PlaidLinkResult) => void
  /** Web: save the flow before Link leaves for an OAuth bank, and resume it on return. */
  oauthResume?: PlaidProviderOAuthResume
}>

function LinkProvided({
  children,
  setOpenLayer,
  setOpenPlaid,
  handleSuccessAsync,
  setIsLayerEligible,
  setStartLayerSession,
  receivedRedirectUri,
  onFallbackStart,
  onFallbackExit,
}: LinkProvidedProps) {
  const {
    linkToken,
    signUpPhoneNumber,
    fallbackLinkToken,
    setFallbackLinkToken,
    setIsPlaidLoading,
    setStartFallbackFlow,
    setIsLayerSessionLoading,
  } = useContext(PlaidContext)
  const queryClient = useQueryClient()
  const { mutateAsync: createLinkToken } = useAccountLinkToken()
  const tokenId = useRef<string | null>(null)

  useEffect(() => {
    tokenId.current = linkToken.tokenId
  }, [linkToken])

  const startFallbackFlow = useCallback(async () => {
    // Layer session is over — hand off to traditional Plaid Link. Clear the
    // Layer in-flight flag so Connect bank buttons don't stay stuck loading
    // after the user later exits the fallback Link (especially on web, where
    // usePlaidLayer.web historically didn't clear this flag).
    setIsLayerSessionLoading(false)
    const verificationId = queryClient.getQueryData<string>(["phoneNumberVerificationId"])
    if (!verificationId) {
      // Happens if Layer fallback raced ahead of OTP confirm (e.g. web used to
      // arm react-plaid-link as soon as Start wrote the link token).
      Sentry.captureMessage("Plaid LinkToken fallback skipped: no verificationId", {
        level: "error",
      })
      setIsPlaidLoading(false)
      return
    }
    const fallbackResponse = await createLinkToken({
      verificationId,
    })

    setFallbackLinkToken({
      linkToken: fallbackResponse.linkToken,
      tokenId: fallbackResponse.tokenId,
    })
  }, [
    createLinkToken,
    queryClient,
    setFallbackLinkToken,
    setIsLayerSessionLoading,
    setIsPlaidLoading,
  ])

  const { startLayer, open } = usePlaidLayer({
    token: linkToken.linkToken,
    onSuccess: (success: LinkSuccess) => {
      Sentry.captureMessage("PlaidLink Layer - Success", { level: "info", extra: { success } })
      handleSuccessAsync({
        success,
        tokenId: tokenId.current as string,
        layerPassed: true,
      })
      if (Platform.OS === "ios") {
        dismissLink()
      }
    },
    onExit: (exit: LinkExit) => {
      Sentry.captureMessage("PlaidLink Layer - Exit", { level: "info", extra: { error: exit } })
      setIsPlaidLoading(false)
      setIsLayerEligible(false)
      setIsLayerSessionLoading(false)
      if (Platform.OS === "ios") {
        dismissLink()
      }
    },
    fallback: startFallbackFlow,
    onLayerReady: (eligible) => {
      setIsLayerEligible(eligible)
    },
  })

  const linkHandle = useCallback(() => {
    if (!linkToken.linkToken) {
      return
    }
    // Guard: refuse to start a session without a phone. If we can't submit,
    // opening Layer will always show the phone-input sheet — same class of
    // bug as the race we're fixing. Log and bail out early instead.
    if (!signUpPhoneNumber) {
      Sentry.captureMessage("PlaidLink Layer - Start skipped: no signUpPhoneNumber", {
        level: "warning",
        extra: { linkToken },
      })
      return
    }
    Sentry.captureMessage("PlaidLink Layer - Start", { level: "info", extra: { linkToken } })
    // Layer sessions are not resumable; drop any record an earlier fallback Link left behind.
    clearPlaidOAuthResume()
    setIsLayerEligible(false)
    setIsLayerSessionLoading(true)
    startLayer({ token: linkToken.linkToken, phoneNumber: signUpPhoneNumber })
  }, [linkToken, signUpPhoneNumber, startLayer, setIsLayerEligible, setIsLayerSessionLoading])

  // Layer must be started from the bank step (`startLayerSession`), not when
  // phone auth writes the link token — otherwise Plaid opens over OTP.

  useEffect(() => {
    if (fallbackLinkToken.linkToken) {
      return
    }
    setOpenLayer(open)
  }, [open, setOpenLayer, fallbackLinkToken])

  useEffect(() => {
    setStartLayerSession(linkHandle)
    setStartFallbackFlow(startFallbackFlow)
  }, [setStartLayerSession, linkHandle, startFallbackFlow, setStartFallbackFlow])

  return (
    <PlaidFallbackOnboardingLink
      linkToken={fallbackLinkToken.linkToken || ""}
      tokenId={fallbackLinkToken.tokenId}
      onSuccess={handleSuccessAsync}
      onStart={onFallbackStart}
      onExit={() => {
        setIsPlaidLoading(false)
        onFallbackExit()
      }}
      launchOnMount={false}
      setOpenPlaid={setOpenPlaid}
      receivedRedirectUri={receivedRedirectUri}
    >
      {children}
    </PlaidFallbackOnboardingLink>
  )
}

export default function PlaidProvider({ children, onLinkResult, oauthResume }: PlaidProviderProps) {
  const queryClient = useQueryClient()
  const posthog = usePostHog()
  const [restored] = useState(() => oauthResume?.restored ?? null)
  const [receivedRedirectUri, setReceivedRedirectUri] = useState(() =>
    restored ? oauthResume?.receivedRedirectUri ?? null : null
  )
  const oauthResumeRef = useRef(oauthResume)
  oauthResumeRef.current = oauthResume

  // Drop leftovers from a prior flow before any Layer effect can see them.
  // Gate reads until after this claim so the first paint cannot start Layer
  // with a stale phone/token (e.g. register deep link → CharityFlow).
  const [hasClaimedFlowCache, setHasClaimedFlowCache] = useState(false)
  useLayoutEffect(() => {
    clearPlaidFlowCache(queryClient)
    if (restored) {
      // Page reloaded after the bank's OAuth redirect; phone auth already ran in the earlier load.
      queryClient.setQueryData(PLAID_FLOW_PHONE_QUERY_KEY, restored.signUpPhoneNumber)
      queryClient.setQueryData(["phoneNumberVerificationId"], restored.verificationId)
    }
    setHasClaimedFlowCache(true)
    return () => {
      clearPlaidFlowCache(queryClient)
    }
  }, [queryClient, restored])

  useEffect(() => {
    if (restored) {
      posthog.capture("plaid_oauth_resume_started")
      Sentry.addBreadcrumb({
        category: "plaid",
        message: "Plaid OAuth resume started",
        level: "info",
      })
    }
  }, [posthog, restored])

  // Phone auth (often above this provider) prefetches phone + link token into
  // the query cache. The bank step reads them and calls `startLayerSession`.
  const cachedPhoneNumber = useCachedQueryData<string>("signUpPhoneNumber")
  const cachedLinkToken = useCachedQueryData<CachedLinkToken>("onboardingLinkToken")
  const signUpPhoneNumber = hasClaimedFlowCache ? cachedPhoneNumber ?? null : null
  const [isPlaidLoading, setIsPlaidLoading] = useState<boolean>(false)
  const [isLayerEligible, setIsLayerEligible] = useState<boolean>(false)
  const [isLayerSessionLoading, setIsLayerSessionLoading] = useState<boolean>(false)
  const openInOnReady = useRef(false)
  const pendingOpenAfterSubmit = useRef(false)
  const events = useRef<{ name: string; timestamp: number }[]>([])
  const bootTimestamp = queryClient.getQueryData<number>(["BOOT_TIMESTAMP"])

  const linkToken = useMemo<LinkProps>(
    () =>
      hasClaimedFlowCache
        ? {
            linkToken: cachedLinkToken?.linkToken ?? null,
            tokenId: cachedLinkToken?.tokenId ?? null,
          }
        : { linkToken: null, tokenId: null },
    [cachedLinkToken, hasClaimedFlowCache]
  )

  const setSignUpPhoneNumber = useCallback(
    (value: string | null) => {
      queryClient.setQueryData(PLAID_FLOW_PHONE_QUERY_KEY, value)
    },
    [queryClient]
  )

  const setLinkToken = useCallback(
    (value: LinkProps) => {
      queryClient.setQueryData(PLAID_FLOW_LINK_TOKEN_QUERY_KEY, value)
    },
    [queryClient]
  )
  const [fallbackLinkToken, setFallbackLinkToken] = useState<LinkProps>(() => ({
    linkToken: restored?.linkToken ?? null,
    tokenId: restored?.tokenId ?? null,
  }))
  const openLayer = useRef<() => void>(() => null)
  const openPlaid = useRef<() => void>(() => null)
  const startLayerSessionRef = useRef<() => void>(() => null)
  const startFallbackFlow = useRef<() => Promise<unknown>>(() => Promise.resolve())
  const { mutateAsync: createAccountLink } = useAccountLink()

  const handleFallbackStart = useCallback(() => {
    if (receivedRedirectUri) {
      return
    }
    // A record from an earlier Link open would hijack this open's OAuth return.
    clearPlaidOAuthResume()
    if (!fallbackLinkToken.linkToken || !fallbackLinkToken.tokenId) {
      return
    }
    const verificationId = queryClient.getQueryData<string>(["phoneNumberVerificationId"])
    if (!verificationId) {
      return
    }
    oauthResumeRef.current?.onBeforeOpen({
      linkToken: fallbackLinkToken.linkToken,
      tokenId: fallbackLinkToken.tokenId,
      verificationId,
      signUpPhoneNumber,
    })
  }, [fallbackLinkToken, queryClient, receivedRedirectUri, signUpPhoneNumber])

  const finishOAuthResume = useCallback(() => {
    clearPlaidOAuthResume()
    if (!receivedRedirectUri) {
      return
    }
    consumePlaidOAuthReturnUri()
    setReceivedRedirectUri(null)
  }, [receivedRedirectUri])

  const handleFallbackExit = useCallback(() => {
    if (receivedRedirectUri) {
      // react-plaid-link keeps the spent redirect URI until the token changes,
      // so let `restart` build a fresh session instead of re-opening this one.
      setFallbackLinkToken({ linkToken: null, tokenId: null })
    }
    finishOAuthResume()
  }, [finishOAuthResume, receivedRedirectUri])
  const onLinkResultRef = useRef(onLinkResult)
  onLinkResultRef.current = onLinkResult

  const appendEvent = useCallback(
    (event: string) => {
      const ev = {
        name: event,
        timestamp: bootTimestamp ? performance.now() - bootTimestamp : performance.now(),
      }
      events.current = [...events.current, ev]
    },
    [bootTimestamp]
  )

  function setOpenLayer(ref: () => void) {
    openLayer.current = ref
  }

  function setOpenPlaid(ref: () => void) {
    openPlaid.current = ref
  }

  function setStartLayerSession(ref: () => void) {
    startLayerSessionRef.current = ref
  }

  function setStartFallbackFlow(ref: () => Promise<unknown>) {
    startFallbackFlow.current = ref
  }

  const startLayerSession = useCallback(() => {
    startLayerSessionRef.current()
  }, [])

  const handleSuccessAsync = async ({
    success,
    tokenId,
    layerPassed,
  }: {
    success: LinkSuccess
    tokenId: string
    layerPassed: boolean
  }) => {
    await createAccountLink({
      tokenId,
      publicToken: success.publicToken,
      linkAccountsMetadata: {
        linkSessionId: success.metadata.linkSessionId,
        institutionId: success.metadata.institution?.id,
        accountIds: success.metadata.accounts.map((acc) => acc.id),
      },
    })
    posthog.capture("linked_bank_account", {
      onboarding_identity_source: layerPassed ? "Layer" : "traditional Plaid",
    })
    if (receivedRedirectUri) {
      posthog.capture("plaid_oauth_resume_succeeded", {
        institution_name: success.metadata.institution?.name ?? null,
      })
    }
    finishOAuthResume()
    setIsPlaidLoading(false)
    Sentry.captureEvent({
      level: "info",
      message: "Plaid onboarding events",
      extra: { events: events.current },
    })

    onLinkResultRef.current({
      layerPassed,
      institutionName: success.metadata.institution?.name,
    })
  }

  const { mutateAsync: createLinkSession } = useAccountLinkSession()

  const restart = useCallback(async () => {
    setIsPlaidLoading(true)

    // ExternalAuth / traditional Link: CheckOtp already gave us a link token
    // via fallbackLinkToken. Re-open it — do not call /Account/LinkSession
    // (that endpoint needs an onboarding OTP verificationId we never have).
    if (fallbackLinkToken.linkToken) {
      openPlaid.current()
      return
    }

    const verificationId = queryClient.getQueryData<string>(["phoneNumberVerificationId"])
    if (!verificationId) {
      Sentry.captureMessage("Plaid restart skipped: no verificationId", { level: "error" })
      setIsPlaidLoading(false)
      return
    }

    setIsLayerEligible(false)
    setIsLayerSessionLoading(true)
    // Do NOT set `openInOnReady = true` here. A stale LAYER_READY from a
    // previous native handler could arrive between now and the new
    // session's `submit()` and would open the fresh, unsubmitted handler
    // — reproducing the "Please submit Phone Number before opening Link"
    // bug. Instead, arm `pendingOpenAfterSubmit`; `usePlaidLayer` flips
    // `openInOnReady` to true only after `submit()` completes for the
    // new session (see `startLayer.onLoad`).
    pendingOpenAfterSubmit.current = true
    openInOnReady.current = false
    const linkSession = await createLinkSession({ verificationId })
    setLinkToken({ tokenId: linkSession.tokenId, linkToken: linkSession.linkToken })
    // Bank step's start effect watches linkToken and calls startLayerSession.
  }, [
    createLinkSession,
    fallbackLinkToken.linkToken,
    queryClient,
    setIsLayerEligible,
    setIsLayerSessionLoading,
    setIsPlaidLoading,
    setLinkToken,
  ])

  return (
    <PlaidContext.Provider
      value={{
        setLinkToken,
        setSignUpPhoneNumber,
        signUpPhoneNumber,
        linkToken,
        openLayer,
        isPlaidLoading,
        setIsPlaidLoading,
        openPlaid,
        fallbackLinkToken,
        setFallbackLinkToken,
        isLayerEligible,
        startLayerSession,
        restart,
        openInOnReady,
        pendingOpenAfterSubmit,
        isLayerSessionLoading,
        setIsLayerSessionLoading,
        appendEvent,
        setStartFallbackFlow,
        startFallbackFlow,
      }}
    >
      <LinkProvided
        setOpenLayer={setOpenLayer}
        setOpenPlaid={setOpenPlaid}
        handleSuccessAsync={handleSuccessAsync}
        setIsLayerEligible={setIsLayerEligible}
        setStartLayerSession={setStartLayerSession}
        receivedRedirectUri={receivedRedirectUri}
        onFallbackStart={handleFallbackStart}
        onFallbackExit={handleFallbackExit}
      >
        {children}
      </LinkProvided>
    </PlaidContext.Provider>
  )
}
